import React, { useMemo } from "react";
import { motion } from "framer-motion";
import SectionHeading from "../SectionHeading";
import { useTranslation } from "react-i18next";
import { useAllTestimonials } from "@/hooks/useTestimonials";
import { Skeleton } from "../ui/skeleton";
import TestimonialCard from "./testimonials/TestimonialCard";
import ScrollVelocity from "../ScrollVelocity/ScrollVelocity";

// Array of user profile images from the public/user directory
const userProfileImages = [
  "/user/Sarah_1.webp",
  "/user/Elena_2.webp",
  "/user/James(1).webp",
  "/user/Olivia_1.webp",
  "/user/z.webp"
];

const TestimonialsSection = () => {
  const { t } = useTranslation();
  const { testimonials, loading, error } = useAllTestimonials();

  // Define the animation variants for testimonial items
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  // Assign profile images sequentially to testimonials
  const testimonialsWithSequentialImages = useMemo(() => {
    if (!testimonials) return [];

    return testimonials.map((testimonial, index) => {
      // Assign image based on index to ensure sequential order
      // Use modulo to handle if there are more testimonials than images
      const imageIndex = index % userProfileImages.length;
      const sequentialImage = userProfileImages[imageIndex];

      // Return a new testimonial object with the sequential image
      return {
        ...testimonial,
        image: sequentialImage
      };
    });
  }, [testimonials]);

  return (
    <div className="testimonials-outer-wrapper py-4 relative z-10">
      {/* Decorative elements for the outer wrapper */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 -left-32 w-64 h-64 border border-anzir/10 rounded-full opacity-10"></div>
        <div className="absolute -bottom-48 -right-48 w-96 h-96 border border-anzir/10 rounded-full opacity-10"></div>
      </div>

      {/* ScrollVelocity component with caution tape style */}
      <div className="velocity-caution">
        <ScrollVelocity
          texts={['MysteryAct ', 'Evidence ']}
          velocity={80}
          className="velocity-text-caution"
          numCopies={10}
          velocityMapping={{ input: [0, 1000], output: [0, 3] }}
        />
      </div>

      <section className="py-16 pb-32 bg-black relative overflow-hidden">
        {/* Background with texture overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/95 to-black/90">
          {/* Detective board texture */}
          <div className="absolute inset-0 bg-[url('/lovable-uploads/85e2274a-758c-4730-8cf7-ed863df93c04.webp')] bg-cover opacity-15 mix-blend-overlay"></div>

          {/* Detective texture pattern */}
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1582139329536-e7284fece509')] bg-repeat opacity-5 mix-blend-multiply"></div>

          {/* Detective magnifying glass decoration */}
          <div className="absolute -bottom-10 -right-10 w-96 h-96 border-2 border-anzir/10 rounded-full opacity-10"></div>
          <div className="absolute -top-10 -left-10 w-72 h-72 border border-white/5 rounded-full opacity-10"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <SectionHeading
              title={t('testimonials.title') || "Testimonial Evidence"}
              description={t('testimonials.description') || "Drag and examine witness statements from our case files"}
              centered={true}
              className="mb-12"
            />
          </motion.div>

          {loading ? (
            <div className="mt-16 flex flex-col md:flex-row gap-8">
              {[...Array(3)].map((_, i) => (
                <Skeleton
                  key={i}
                  className="flex-1 h-80 rounded-xl bg-white/5 border border-white/10"
                />
              ))}
            </div>
          ) : error ? (
            <div className="mt-16 text-center py-12 bg-black/50 backdrop-blur-md rounded-lg border border-red-900/30 max-w-4xl mx-auto">
              <p className="text-red-400">Error loading evidence files</p>
            </div>
          ) : testimonialsWithSequentialImages && testimonialsWithSequentialImages.length > 0 ? (
            <div className="mt-12 px-4">
              <div className="relative w-full min-h-[500px] bg-black/20 rounded-lg border border-white/10 p-6">
                {/* Detective bulletin board */}
                <div className="absolute inset-0 bg-[url('/lovable-uploads/85e2274a-758c-4730-8cf7-ed863df93c04.webp')] bg-cover opacity-10"></div>

                {/* Red yarn connections - hidden on mobile */}
                <div className="absolute inset-0 hidden md:block">
                  <div className="absolute top-1/4 left-1/4 w-full h-0.5 bg-red-600/20 -rotate-12 transform origin-left"></div>
                  <div className="absolute top-1/3 right-1/4 w-full h-0.5 bg-red-600/20 rotate-12 transform origin-right"></div>
                  <div className="absolute bottom-1/3 left-1/3 w-1/2 h-0.5 bg-red-600/20 rotate-45 transform origin-left"></div>
                </div>

                {/* Testimonial cards - vertical on mobile, horizontal on desktop */}
                <div className="flex flex-col md:flex-row md:justify-center md:items-center mb-16 gap-4 md:gap-0">
                  {testimonialsWithSequentialImages.slice(0, Math.min(testimonialsWithSequentialImages.length, 5)).map((testimonial, index) => (
                    <div
                      key={testimonial.id}
                      className="w-full md:flex-shrink-0 md:mx-1 my-3 md:my-0"
                      style={{
                        width: 'calc(100% - 6px)',
                        transform: `rotate(${index % 2 === 0 ? '-' : ''}${Math.min(1, 2 + index)}deg)`, 
                        zIndex: 10 + index,
                        marginLeft: index > 0 && window.innerWidth >= 768 ? '-10px' : '0',
                        ...(window.innerWidth >= 768 ? { width: 'calc(19% - 6px)' } : {})
                      }}
                    >
                      <TestimonialCard
                        testimonial={testimonial}
                        itemVariants={itemVariants}
                        initialPosition={{ x: 0, y: 0 }}
                        index={index}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-16 text-center py-12 bg-black/50 backdrop-blur-md rounded-lg border border-white/10 max-w-4xl mx-auto">
              <p className="text-white/70">No witness statements available</p>
            </div>
          )}
        </div>
      </section>

      {/* New ScrollVelocity component after the testimonials section with caution tape style */}
      <div className="velocity-caution">
        <ScrollVelocity
          texts={['Evidence ', 'MysteryAct ']}
          velocity={80}
          className="velocity-text-caution"
          numCopies={10}
          velocityMapping={{ input: [0, 1000], output: [0, 3] }}
        />
      </div>
    </div>
  );
};

export default TestimonialsSection;
